import { useEffect, useState } from "react";
import { FileArrowDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import TxnPeriodFilter, { periodRange, inRange } from "../../components/TxnPeriodFilter";
import { downloadStatement } from "../../lib/download";
import { inr, fmtDate, txnBadge } from "../../lib/format";
import AppLayout, { PageLoader, EmptyState, thCls, tdCls, rowCls } from "../../components/AppLayout";
import { Badge } from "../../components/ui/badge";

const FILTERS = ["All", "Investment", "Additional Investment", "Redemption", "Withdrawal", "Dividend", "Interest", "Fee"];

export default function TransactionsPage() {
  const [txns, setTxns] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("All");
  const [mode, setMode] = useState("All");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const applyPeriod = () => {
    if (mode === "Custom") {
      if (!from || !to) { toast.error("Select both From and To dates."); return; }
      if (to < from) { toast.error("To Date cannot be earlier than From Date."); return; }
    }
  };

  useEffect(() => {
    api.get("/investor/transactions").then((r) => setTxns(r.data)).catch((e) => setError(apiError(e)));
  }, []);

  if (error) return <AppLayout title="Transactions"><p className="text-red-400 text-sm">{error}</p></AppLayout>;
  if (!txns) return <AppLayout title="Transactions"><PageLoader /></AppLayout>;

  const filtered = txns.filter((t) => (filter === "All" || t.type === filter) && inRange(t.date, mode, from, to));

  return (
    <AppLayout title="Transactions" subtitle={`${txns.length} transactions on record`}>
      <div className="fade-up space-y-5">
        <TxnPeriodFilter mode={mode} setMode={setMode} from={from} setFrom={setFrom} to={to} setTo={setTo}
          onApply={applyPeriod} onReset={() => { setMode("All"); setFrom(""); setTo(""); }} testId="inv-txn-period" />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2" data-testid="txn-filters">
          {FILTERS.map((f) => (
            <button key={f} data-testid={`txn-filter-${f.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setFilter(f)}
              className={`text-[11px] font-semibold rounded-full px-3.5 py-1.5 border transition-colors duration-200 ${
                filter === f ? "pill-active" : "border-white/10 text-zinc-500 hover:text-zinc-200 hover:border-white/20"
              }`}>
              {f}
            </button>
          ))}
          </div>
          <button data-testid="download-txn-statement"
            onClick={() => {
              const [f, t] = periodRange(mode, from, to);
              const qs = f || t ? `?from_date=${f || ""}&to_date=${t || ""}` : "";
              downloadStatement(`/statements/my/transactions.pdf${qs}`, "KMC-Transaction-Statement.pdf");
            }}
            className="flex items-center gap-2 text-xs font-bold rounded-lg px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-[#030706] emerald-glow transition-colors duration-200">
            <FileArrowDown size={15} weight="bold" /> Statement (PDF)
          </button>
        </div>
        <div className="glass-card overflow-hidden">
          {filtered.length === 0 ? <EmptyState message="No transactions match this filter." /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="transactions-table">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className={thCls}>Date</th>
                    <th className={thCls}>Type</th>
                    <th className={thCls}>Description</th>
                    <th className={thCls}>Reference</th>
                    <th className={`${thCls} text-right`}>Amount</th>
                    <th className={`${thCls} text-right`}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((t) => (
                    <tr key={t.id} className={rowCls}>
                      <td className={`${tdCls} tnum text-zinc-300 whitespace-nowrap`}>{fmtDate(t.date)}</td>
                      <td className={`${tdCls} text-xs font-semibold text-zinc-300`}>{t.type}</td>
                      <td className={`${tdCls} text-zinc-400`}>{t.description}</td>
                      <td className={`${tdCls} tnum text-[11px] text-zinc-600`}>{t.reference_number || "—"}</td>
                      <td className={`${tdCls} text-right tnum text-zinc-100`}>{inr(t.amount)}</td>
                      <td className={`${tdCls} text-right`}>
                        <Badge variant="outline" className={`text-[10px] ${txnBadge(t.status)}`}>{t.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
